import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

const FeatureCard = ({ icon, title, description, isDarkMode, iconSize }) => {
  return (
    <motion.div 
      className="relative p-[1px] rounded-[40px] w-full max-w-[536px] cursor-pointer h-auto aspect-[536/454] font-lexend overflow-hidden group"
    >
      {/* Gradient Border Background */}
      <div className={`absolute inset-0 rounded-[40px] ${
        isDarkMode 
          ? 'bg-gradient-to-b from-[#FBCD40] to-[#000000]'
          : 'bg-gradient-to-b from-[#FBCD40] to-[#FFFFFF]'
      } dark:opacity-100 opacity-30`} />
      
      {/* Background Layer */}
      <div className={`absolute inset-[1px] rounded-[39px] ${
        isDarkMode ? 'bg-black' : 'bg-white'
      } opacity-80`} />
      
      {/* Content Container */}
      <div className="relative rounded-[40px] h-full flex flex-col">
        {/* Top Section (60%) - Grid and Icon */}
        <div className="h-[60%] relative w-full">
          {/* Grid Layer */}
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Default Grid */}
            <div className="w-full h-full relative">
              <img 
                src="/assets/Grid default.png"
                alt="grid"
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300 group-hover:opacity-0 opacity-70"
              />
              <img 
                src="/assets/Grid hover.png"
                alt="grid hover"
                className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-300 group-hover:opacity-70"
              />
              
              {/* Gradient Circles */}
              <div className="absolute inset-0 flex items-center justify-center">
                {/* Top-Left Circle - 56x56 - Moves Furthest */}
                <div 
                  className="absolute w-[8%] aspect-square min-w-[35px] min-h-[35px] max-w-[56px] max-h-[56px] rounded-full bg-gradient-to-b from-[#FCD535] to-[#F49097] transition-all duration-700 transform opacity-0 group-hover:opacity-100
                  -translate-x-8 -translate-y-8
                  group-hover:-translate-x-16 group-hover:-translate-y-16 
                  md:group-hover:-translate-x-20 md:group-hover:-translate-y-20 
                  lg:group-hover:-translate-x-24 lg:group-hover:-translate-y-24"
                />
                {/* Top-Right Circle - 35x35 - Moves Medium Distance */}
                <div 
                  className="absolute w-[6%] aspect-square min-w-[35px] min-h-[35px] max-w-[35px] max-h-[35px] rounded-full bg-gradient-to-b from-[#FCD535] to-[#F49097] transition-all duration-700 transform opacity-0 group-hover:opacity-100
                  translate-x-6 -translate-y-6
                  group-hover:translate-x-14 group-hover:-translate-y-14
                  md:group-hover:translate-x-16 md:group-hover:-translate-y-16
                  lg:group-hover:translate-x-20 lg:group-hover:-translate-y-20"
                />
                {/* Bottom-Right Circle - 56x56 - Moves Furthest */}
                <div 
                  className="absolute w-[8%] aspect-square min-w-[35px] min-h-[35px] max-w-[56px] max-h-[56px] rounded-full bg-gradient-to-b from-[#FCD535] to-[#F49097] transition-all duration-700 transform opacity-0 group-hover:opacity-100
                  translate-x-8 translate-y-8
                  group-hover:translate-x-16 group-hover:translate-y-16
                  md:group-hover:translate-x-20 md:group-hover:translate-y-20
                  lg:group-hover:translate-x-24 lg:group-hover:translate-y-24"
                />
                {/* Bottom-Left Circle - 35x35 - Moves Medium Distance */}
                <div 
                  className="absolute w-[6%] aspect-square min-w-[35px] min-h-[35px] max-w-[35px] max-h-[35px] rounded-full bg-gradient-to-b from-[#FCD535] to-[#F49097] transition-all duration-700 transform opacity-0 group-hover:opacity-100
                  -translate-x-6 translate-y-6
                  group-hover:-translate-x-14 group-hover:translate-y-14
                  md:group-hover:-translate-x-16 md:group-hover:translate-y-16
                  lg:group-hover:-translate-x-20 lg:group-hover:translate-y-20"
                />
              </div>
              
              {/* Centered Icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div 
                  className="transition-transform duration-300 group-hover:scale-[0.65] group-hover:translate-y-[15%] w-[25%] max-w-[171px]"
                  style={{ 
                    aspectRatio: `${iconSize.width}/${iconSize.height}`,
                  }}
                >
                  <img 
                    src={icon} 
                    alt={title} 
                    className="w-full h-full object-contain z-10"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section (40%) - Text Content */}
        <div className="h-[40%] p-4 md:p-6 lg:p-8 flex flex-col justify-center transition-transform duration-300 group-hover:-translate-y-2.5">
          <h3 className={`text-[20px] md:text-[26px] font-semibold mb-2 md:mb-3 ${
            isDarkMode ? 'text-white' : 'text-black'
          } font-lexend text-left`}>
            {title}
          </h3>
          <p className={`text-[14px] md:text-[18px] leading-relaxed font-lexend ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          } text-left`}>
            {description}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const WhyJoinSection = () => {
  const { isDarkMode } = useTheme();
  
  const features = [
    {
      icon: "/assets/Icon 1.png",
      title: "Level Up Your Skills",
      description: "Work on real-world problems to sharpen your AI/ML expertise",
      iconSize: { 
        width: 171, 
        height: 197,
        hoverScale: 0.65
      }
    },
    {
      icon: "/assets/Icon 2.png",
      title: "Earn Rewards and Recognition",
      description: "Compete for prizes and establish your reputation",
      iconSize: { 
        width: 180, 
        height: 203,
        hoverScale: 0.65
      }
    },
    {
      icon: "/assets/Icon 3.png",
      title: "Build Your Portfolio",
      description: "Create projects that demonstrate your abilities to employers or clients",
      iconSize: { 
        width: 160, 
        height: 208,
        hoverScale: 0.65
      }
    },
    {
      icon: "/assets/Icon 4.png",
      title: "Be Part of a Community",
      description: "Collaborate with peers worldwide, learn together and grow",
      iconSize: { 
        width: 208, 
        height: 177,
        hoverScale: 0.65
      }
    }
  ];

  return (
    <section 
      id="why-join"
    className={`relative py-16 px-4 z-0 font-lexend flex justify-center ${
      isDarkMode ? 'bg-[#000000]' : 'bg-white'
    }`}>
      <div className="max-w-[1300px] mx-auto">
        <motion.h2 
          className={`text-[36px] font-bold text-center mb-8 md:mb-12 lg:mb-20 font-lexend ${
            isDarkMode ? 'text-white' : 'text-black'
          }`}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Why join lunor.quest?
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8 justify-items-center">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="w-full"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <FeatureCard {...feature} isDarkMode={isDarkMode} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyJoinSection; 